import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { AuthUtils } from '@core/auth/auth.utils';
import { BrowserNavigator } from '@core/browser/browser-navigator.service';
import { environment } from '@environments/environments';
import { OfficeTenantsService } from '@office/tenants/office-tenants.service';
import { ErrorCodes } from '@shared/enums/error-code.enum';
import { errorHandler } from '@shared/functions/utils.type';
import { HttpBody, InterceptedHttpErrorBody, ProjectSettingSaveErrorBody } from '@shared/models/domain/custom-views.model';
import { AlertService } from '@shared/services/alert.service';
import { catchError, Observable, throwError } from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private _authService = inject(AuthService);
    private officeTenantsService = inject(OfficeTenantsService);
    private alertService = inject(AlertService);
    private router = inject(Router);
    private browserNavigator = inject(BrowserNavigator);


    /**
     * Intercept
     *
     * @param req
     * @param next
     */
    intercept(req: HttpRequest<HttpBody>, next: HttpHandler): Observable<HttpEvent<HttpBody>> {
        // Clone the request object
        let newReq = req.clone();
        const websiteId = this.officeTenantsService.getTenantDetailsBySlug()?.tenantId || environment.defaultWebsiteId;
        // Request
        //
        // If the access token didn't expire, add the Authorization header.
        // We won't add the Authorization header if the access token expired.
        // This will force the server to return a "401 Unauthorized" response
        // for the protected API routes which our response interceptor will
        // catch and delete the access token from the local storage while logging
        // the user out from the app.
        if (this._authService.accessToken && !AuthUtils.isTokenExpired(this._authService.accessToken)) {
            newReq = req.clone({
                headers: req.headers
                .set('Authorization', 'Bearer ' + this._authService.accessToken)
                .set('X-Custom-Website-Id', String(websiteId))
            });
        }

        // Response
        return next.handle(newReq).pipe(
            catchError((error: HttpErrorResponse | Error | string) => {
                const interceptRequest = !newReq.headers.get('Intercept') || newReq.headers.get('Intercept') === 'true';
                // Catch "401 Unauthorized" responses
                if (error instanceof HttpErrorResponse && error.status === 401 && interceptRequest) {
                    // Sign out
                    this._authService.signOut();

                    // Reload the app
                    if (!newReq?.url?.includes('/login')) {
                        this.browserNavigator.reload();
                    }
                }
                // Catch "500 Internal Server Error" or "400 Bad Request" responses
                else if (error instanceof HttpErrorResponse && (error.status === 500 || error.status === 400) && interceptRequest) {
                    if ((error.error as InterceptedHttpErrorBody)?.ExceptionMessage) {
                        if ((error.error as InterceptedHttpErrorBody)?.Exception === "Platform.Api.v3.Business.Framework.AppAuthException") {
                            errorHandler(ErrorCodes.UserNotFound, this.alertService);
                        } else {
                            this.alertService.errorToast('System Error', (error.error as ProjectSettingSaveErrorBody)?.ExceptionMessage, 2000);
                        }
                    }
                    else if ((error.error as InterceptedHttpErrorBody)?.title) {
                        this.alertService.errorToast('Validation Error', (error.error as ProjectSettingSaveErrorBody)?.title, 2000);
                    }
                }
                // Catch "403 Forbidden" responses
                else if (error instanceof HttpErrorResponse && error.status === 403 && interceptRequest) {
                    if (this._authService.accessToken && !AuthUtils.isTokenExpired(this._authService.accessToken) && this._authService.userProfile) {
                        const defaultRoute = 'not-authorized';
                        if (!this.router?.url?.includes(defaultRoute)) {
                            this.officeTenantsService.tenantSlugNavigate(defaultRoute);
                        }
                    }
                }
                

                return throwError(() => error);
            })
        );
    }
}
